/**
 * The cart's charge, composed in WHOLE CENTS by the server's rule.
 *
 * Bug Intake #481, gate round 3 (F1/F2). Until the server's quote lands the
 * cart shows -- and posts -- its own figure, and the checkout guard refuses
 * one more than a cent off the server's. So the twin follows the server's ONE
 * rule exactly (`api/src/pricing/order_money.py`): every component is whole
 * cents, ROUND_HALF_UP on the DECIMAL form of its value, in the server's
 * order:
 *
 *   items + sales tax - coupon - allowance + shipping - free item
 *         + Stripe fee, GROSSED UP (2.9% + 0.30 of the whole card charge,
 *           the fee itself and the shopper-paid platform fee included;
 *           card stores that collect it)
 *         + platform fee (when the shopper pays it)
 *         then Stripe's $0.50 minimum on a card charge.
 *
 * "Decimal form" is the shortest string that round-trips the double --
 * `String(x)` here, `str(x)` in Python -- so 5.775 rounds to 5.78 on both
 * sides even though the double is a hair under 5.775. The arithmetic on it is
 * exact (BigInt), never `x * 100` in floating point.
 *
 * Both sides are held to one table: api/tests/fixtures/cart_charge_parity.json
 * (cart-charge-parity.spec.js here, test_cart_charge_parity.py there).
 */

/** `{ n, scale }` with value === n / 10**scale, exactly, for a finite number. */
function decimalParts(value) {
  const text = String(Number(value));
  const match = /^(-?)(\d*)(?:\.(\d*))?(?:e([+-]?\d+))?$/i.exec(text);
  if (!match) return { n: 0n, scale: 0 };
  const [, sign, whole, frac = "", exp = "0"] = match;
  let n = BigInt(`${whole || "0"}${frac}`);
  let scale = frac.length - Number(exp);
  if (scale < 0) {
    n *= 10n ** BigInt(-scale);
    scale = 0;
  }
  return { n: sign === "-" ? -n : n, scale };
}

/** num / den rounded to an integer, ROUND_HALF_UP (halves away from zero). */
function divHalfUp(num, den) {
  const negative = num < 0n !== den < 0n;
  const a = num < 0n ? -num : num;
  const b = den < 0n ? -den : den;
  let q = a / b;
  if ((a % b) * 2n >= b) q += 1n;
  return Number(negative ? -q : q);
}

/** Whole cents of a dollar amount, ROUND_HALF_UP on its decimal form. */
export function halfUpCents(dollars) {
  const value = Number(dollars);
  if (!Number.isFinite(value)) return 0;
  const { n, scale } = decimalParts(value);
  return divHalfUp(n * 100n, 10n ** BigInt(scale));
}

/** `cents x factor`, rounded to whole cents ROUND_HALF_UP (exact). */
export function mulHalfUpCents(cents, factor) {
  const { n, scale } = decimalParts(factor);
  return divHalfUp(BigInt(cents) * n, 10n ** BigInt(scale));
}

/** `pct` percent of `cents`, rounded to whole cents ROUND_HALF_UP (exact). */
export function percentOfCentsHalfUp(cents, pct) {
  const { n, scale } = decimalParts(pct);
  return divHalfUp(BigInt(cents) * n, 10n ** BigInt(scale + 2));
}

/** Stripe's processing fee on a charge of `cents`: 2.9% + 0.30. */
export function stripeFeeCents(cents) {
  return cents > 0 ? mulHalfUpCents(cents, 0.029) + 30 : 0;
}

/**
 * The processing fee that covers Stripe's cut of the WHOLE card charge
 * (Jake, 2026-10-29, Trello #633: ALL fees gross up). `baseCents` is the card
 * charge before the processing fee, plus the platform fee when the shopper
 * pays it. Returns the smallest whole-cent P with
 * `P >= stripeFeeCents(baseCents + P)` -- the fee on the fee is in it. Twin of
 * `gross_up_processing_fee` in api/src/pricing/order_money.py.
 */
export function grossUpFeeCents(baseCents) {
  if (!(baseCents > 0)) return 0;
  // Closed-form start ((0.029 B + 30) / 0.971), then walk to the exact cent.
  let p = Math.max(Math.round((0.029 * baseCents + 30) / 0.971), 0);
  while (p < stripeFeeCents(baseCents + p)) p += 1;
  while (p > 0 && p - 1 >= stripeFeeCents(baseCents + p - 1)) p -= 1;
  return p;
}

/**
 * Compose the charge. Returns integer cents: `chargedCents`, the components
 * as added (`salesTaxCents`, `couponCents`, `processingFeeCents` -- the
 * Stripe fee, 0 unless it is collected -- and `minimumChargeCents`), plus
 * `preFeeCents` (the charge the Stripe fee is computed on) and
 * `stripeFeeCents` (that fee whether or not the store collects it, for the
 * cart's display).
 */
export function composeCartChargeCents({
  items = 0,
  collectSalesTax = false,
  salesTax = 0,
  couponType = null,
  couponAmount = 0,
  allowance = 0,
  shippingEnabled = false,
  shipping = 0,
  freeItemCents = 0,
  isCard = false,
  collectProcessingFee = false,
  platformFeeCents = 0,
} = {}) {
  let charge = halfUpCents(items);

  let salesTaxCents = 0;
  if (collectSalesTax && Number(salesTax) > 0) {
    salesTaxCents = halfUpCents(salesTax);
    charge += salesTaxCents;
  }

  let couponCents = 0;
  const amount = Number(couponAmount);
  if (couponType && Number.isFinite(amount) && amount > 0) {
    if (couponType === "$") {
      couponCents = Math.min(halfUpCents(amount), Math.max(charge, 0));
    } else {
      couponCents = percentOfCentsHalfUp(Math.max(charge, 0), Math.min(amount, 100));
    }
    charge -= couponCents;
  }

  charge -= halfUpCents(allowance);

  if (shippingEnabled && Number(shipping)) {
    charge += halfUpCents(shipping);
  }

  const free = Math.max(Math.round(Number(freeItemCents) || 0), 0);
  if (free > 0) {
    charge -= Math.min(free, Math.max(charge, 0));
  }

  const preFeeCents = charge;
  const platformCents = Math.max(Math.round(Number(platformFeeCents) || 0), 0);
  // The card charge the fee is computed on: everything but the fee itself,
  // INCLUDING the platform fee the shopper pays (callers pass 0 when the
  // distributor absorbs it -- then it is not part of the card charge).
  const feeBaseCents = charge + platformCents;
  let processingFeeCents = 0;
  let fee = 0;
  if (isCard && collectProcessingFee) {
    processingFeeCents = grossUpFeeCents(feeBaseCents);
    fee = processingFeeCents;
    charge += processingFeeCents;
  } else if (isCard) {
    // Display only (the line is gated off downstream): the plain fee.
    fee = stripeFeeCents(preFeeCents);
  }

  charge += platformCents;

  let minimumChargeCents = 0;
  if (isCard && charge > 0 && charge < 50) {
    minimumChargeCents = 50 - charge;
    charge = 50;
  }

  return {
    chargedCents: charge,
    salesTaxCents,
    couponCents,
    processingFeeCents,
    minimumChargeCents,
    preFeeCents,
    stripeFeeCents: fee,
  };
}
